import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { TeamVm, TeamRole } from 'api/management/team.vm';
import { GetTeamsVm } from 'api/management/get-teams.vm';
import { getTeams } from 'api/management/get-teams';
import { createTeam, CreateTeamDto } from 'api/management/create-team';
import { getWarehouses } from 'api/warehouse/get-warehouses';
import { GetWarehousesVm } from 'api/warehouse/get-warehouses.vm';
import { useSnackbar } from 'hooks/use-snackbar';
import CreateTeamDialog from 'pages/management/CreateTeamDialog';

const TeamsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [teams, setTeams] = useState<TeamVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string | null>(searchParams.get('warehouse'));
  const [selectedRole, setSelectedRole] = useState<string | null>(searchParams.get('role'));
  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const snackbar = useSnackbar();

  const fetchTeams = (offset: string | null, reset: boolean = false) => {
    setLoading(true);

    getTeams(offset || '', selectedWarehouse || '', selectedRole || '', {
      success: (response: GetTeamsVm) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setTeams((prevTeams) => (reset ? response.objects : [...prevTeams, ...response.objects]));
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter equipas:', err);
        setLoading(false);
      },
    });
  };

  const fetchWarehouses = () => {
    getWarehouses('', {
      success: (response: GetWarehousesVm) => {
        const warehouseList = response.objects.map((w) => ({ id: w.id, name: w.name }));
        setWarehouses(warehouseList);

        // Auto-select the first warehouse if available and none is selected from URL
        if (warehouseList.length > 0 && !selectedWarehouse) {
          setSelectedWarehouse(warehouseList[0].id);
        }
      },
      error: (err) => {
        console.error('Falha ao obter armazéns:', err);
      },
    });
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  useEffect(() => {
    // Only fetch teams if we have a warehouse selected
    if (selectedWarehouse) {
      fetchTeams(null, true);

      // Update URL params
      const params = new URLSearchParams();
      if (selectedWarehouse) params.set('warehouse', selectedWarehouse);
      if (selectedRole) params.set('role', selectedRole);
      setSearchParams(params);
    }
  }, [selectedWarehouse, selectedRole, setSearchParams]);

  const handleLoadMore = () => {
    if (!hasMore || !nextOffset) return;
    // Load more - append to existing teams (reset = false)
    fetchTeams(nextOffset, false);
  };

  const handleClearFilters = () => {
    // Clear role filter but keep warehouse intact
    setSelectedRole(null);
  };

  const handleCreateTeam = (data: { name: string; role: TeamRole; warehouseId: string }) => {
    setCreateLoading(true);
    const createDto: CreateTeamDto = {
      name: data.name,
      role: data.role,
      warehouseId: data.warehouseId,
    };

    createTeam(createDto, {
      success: () => {
        setCreateLoading(false);
        setCreateDialogOpen(false);
        snackbar.show('Equipa criada com sucesso', null, 'success');
        // Refresh teams list
        fetchTeams(null, true);
      },
      error: (err) => {
        setCreateLoading(false);
        console.error('Falha ao criar equipa:', err);
        snackbar.show('Falha ao criar equipa', null, 'error');
      },
    });
  };

  const handleRowClick = (teamId: string) => {
    navigate(`/management/teams/${teamId}`);
  };

  const getWarehouseName = (warehouseId: string | null) => {
    if (!warehouseId) return undefined;
    const warehouse = warehouses.find((w) => w.id === warehouseId);
    return warehouse?.name;
  };

  const getRoleLabel = (role: TeamRole) => {
    const roleConfig = {
      [TeamRole.INVENTORY]: 'Inventário',
      [TeamRole.RECEPTION]: 'Receção',
      [TeamRole.PICKING]: 'Expedição',
      [TeamRole.PRODUCTION]: 'Produção',
    };

    return roleConfig[role];
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Equipas
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateDialogOpen(true)}>
          Criar Equipa
        </Button>
      </Box>

      <Paper sx={{ mb: 2, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Armazém</InputLabel>
              <Select value={selectedWarehouse || ''} label="Armazém" onChange={(e) => setSelectedWarehouse(e.target.value || null)}>
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Função</InputLabel>
              <Select value={selectedRole || ''} label="Função" onChange={(e) => setSelectedRole(e.target.value || null)}>
                <MenuItem value="">Todas as funções</MenuItem>
                <MenuItem value={TeamRole.INVENTORY}>Inventário</MenuItem>
                <MenuItem value={TeamRole.RECEPTION}>Receção</MenuItem>
                <MenuItem value={TeamRole.PICKING}>Expedição</MenuItem>
                <MenuItem value={TeamRole.PRODUCTION}>Produção</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button variant="outlined" onClick={handleClearFilters} disabled={!selectedRole} fullWidth sx={{ height: '56px' }}>
              Limpar Filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Função</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teams.map((team) => (
              <TableRow key={team.id} hover onClick={() => handleRowClick(team.id)} sx={{ cursor: 'pointer' }}>
                <TableCell>{team.name}</TableCell>
                <TableCell>{getRoleLabel(team.role)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : hasMore ? (
          <Button variant="outlined" onClick={handleLoadMore} disabled={loading || !nextOffset}>
            Carregar mais
          </Button>
        ) : teams.length > 0 ? (
          <Typography variant="body2" color="textSecondary">
            Não existem mais equipas para carregar
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            {selectedWarehouse || selectedRole ? 'Nenhuma equipa encontrada com os filtros aplicados' : 'Nenhuma equipa disponível'}
          </Typography>
        )}
      </Box>

      <CreateTeamDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateTeam}
        loading={createLoading}
        preselectedWarehouseId={selectedWarehouse}
        preselectedWarehouseName={getWarehouseName(selectedWarehouse)}
      />
    </Box>
  );
};

export default TeamsPage;
